import { Loader } from '../Loader';
import { Sidebar } from '../Sidebar';
import { openInspirationSidebar, refreshInspirationShoppingList } from './inspiration-api';
import { inspirationUrlStateMethods } from './url-state';
import { inspirationElasticMethods } from './elastic';
import { listingUrlStateMethods } from '../listing/url-state';
import { listingRendererMethods } from '../listing/renderer';
import { listingFiltersMethods } from '../listing/filters';
import { listingFilterAccordionMethods } from '../listing/filterAccordion';
import { inspirationEventsMethods } from './events';

export class InspirationsPage {
    constructor() {
        this.root = document.querySelector('[data-inspirations]');

        if (!this.root) {
            return;
        }

        this.title = this.root.querySelector('[data-listing-title]');
        this.description = this.root.querySelector(
            '[data-listing-description]'
        );
        this.total = this.root.querySelector('[data-listing-total]');
        this.termsSection = this.root.querySelector('[data-listing-terms]');
        this.termsTrack = this.root.querySelector('[data-listing-terms-track]');
        this.termsTrackWrapper = this.root.querySelector(
            '[data-listing-terms-track-wrapper]'
        );
        this.termsBackSlot = this.root.querySelector(
            '[data-listing-terms-back-slot]'
        );
        this.filtersSection = this.root.querySelector(
            '[data-listing-active-filters-section]'
        );
        this.filtersTrack = this.root.querySelector(
            '[data-listing-active-filters]'
        );
        this.results = this.root.querySelector('[data-listing-results]');
        this.resultsPagination = this.root.querySelector(
            '[data-listing-pagination]'
        );
        this.loader = new Loader();
        this.filtersSidebarContent = this.root.querySelector(
            '[data-listing-filters-sidebar-content]'
        );
        this.desktopFiltersRoot = this.root.querySelector(
            '[data-listing-desktop-filters]'
        );
        this.filtersSidebar = null;
        this.inspirationSidebar = null;
        this.activeInspirationTermId = 0;
        this.sortRoot = this.root.querySelector('[data-listing-sort]');
        this.sortToggle = this.root.querySelector('[data-listing-sort-toggle]');
        this.sortMenu = this.root.querySelector('[data-listing-sort-menu]');
        this.sortLabel = this.root.querySelector('[data-listing-sort-label]');
        this.sortIcon = this.root.querySelector('[data-listing-sort-icon]');
        this.bootstrap =
            typeof window !== 'undefined' && window.mdaInspirations
                ? window.mdaInspirations
                : {};
        this.ssrState = this.bootstrap.ssrState || {
            page: Number(this.resultsPagination?.dataset.page || 1),
            sort: 'newest',
            filters: {},
        };
        this.state = this.createInitialState();
        this.pendingFilters = {};
        this.pendingFiltersCountTimer = null;
        this.pendingFiltersCountRequestId = 0;
        this.filterCountsLoading = false;
        this.handleInteraction = this.onRootClick.bind(this);
        this.handleDocumentClick = this.onDocumentClick.bind(this);
        this.handleWindowResize = this.onWindowResize.bind(this);

        this.bindEvents();
        this.replaceHistoryState();
        this.initFiltersSidebar();
        this.updateSortUi(this.state.sort);
        this.syncInitialStateFromUrl();
        this.filterCountsLoaded =
            !this.bootstrap.filterCountsSsr || this.hasFilterCountsInSidebar();
        this.elasticRequestLogged = false;

        if (this.canUseElastic() && !this.filterCountsLoaded) {
            queueMicrotask(() => {
                void this.loadFilterTermCounts();
            });
        }

        window.mdaInspirationsPage = this;
    }

    refreshActiveInspirationSidebar() {
        return refreshInspirationShoppingList(this);
    }

    syncInitialStateFromUrl() {
        if (!this.canUseElastic() || !this.shouldRefetchAfterSsr()) {
            return;
        }

        this.fetchAndRender({ pushUrl: null, showLoader: true });
    }

    createInitialState() {
        return {
            taxonomy: '',
            termId: 0,
            page: Number(this.resultsPagination?.dataset.page || 1),
            sort: this.getSortFromUrl() || this.ssrState.sort,
            filters: this.getFiltersFromUrl(),
            phrase: '',
            type:
                this.bootstrap?.inspirationsType ||
                this.getInspirationsTypeFromUrl(),
        };
    }

    isSearchMode() {
        return false;
    }

    isInspirationsMode() {
        return true;
    }

    isDesignersInspirationsView(state = this.state) {
        const type =
            state?.type ||
            this.bootstrap?.inspirationsType ||
            this.getInspirationsTypeFromUrl();

        return type === 'projektanci';
    }

    async openInspirationSidebar(termId) {
        this.loader?.show();

        try {
            await openInspirationSidebar(this, termId);
        } finally {
            this.loader?.hide();
        }
    }

    getPhraseFromBootstrap() {
        return '';
    }

    bindEvents() {
        this.root.addEventListener('click', this.handleInteraction);
        window.addEventListener('popstate', this.onPopState.bind(this));
        window.addEventListener('resize', this.handleWindowResize);
        document.addEventListener('click', this.handleDocumentClick);
    }

    replaceHistoryState() {
        window.history.replaceState(
            {
                listingState: { ...this.state },
            },
            '',
            window.location.href
        );
    }

    initFiltersSidebar() {
        if (!this.filtersSidebarContent) {
            this.filtersSidebarContent = this.root?.querySelector(
                '[data-listing-filters-sidebar-content]'
            );
        }

        if (!this.filtersSidebarContent || this.filtersSidebar) {
            return;
        }

        this.filtersSidebar = new Sidebar({
            overlayClassNames: ['bg-grey-900/60'],
        });

        this.filtersSidebar.content.appendChild(this.filtersSidebarContent);
        this.filtersSidebar.content.addEventListener(
            'click',
            this.handleInteraction
        );
        this.syncPendingFiltersFromState();
    }

    ensureFiltersSidebar() {
        if (!this.filtersSidebar) {
            this.initFiltersSidebar();
        }

        return Boolean(this.filtersSidebar);
    }

    async fetchAndRender({
        pushUrl = null,
        appendResults = false,
        showLoader = true,
    } = {}) {
        if (showLoader) {
            this.root.classList.add('is-loading');
            this.loader?.show();
        }

        this.setLoadMoreLoading(true);

        try {
            const data = await this.fetchListingPayload(this.state);

            if (appendResults) {
                this.renderResults(data.results || [], data.pagination || {}, {
                    append: true,
                });

                const total = Number(data.total || 0);
                const displayedCount = this.getDisplayedProductCount(total);
                const perPage = Number(
                    data.pagination?.perPage || this.bootstrap.perPage || 16
                );
                const maxPages = total > 0 ? Math.ceil(total / perPage) : 1;
                const pagination = {
                    ...(data.pagination || {}),
                    page: this.state.page,
                    perPage,
                    total,
                    maxPages,
                    current: displayedCount,
                    hasMore: displayedCount < total,
                };

                this.renderResultsPagination(pagination, { append: true });

                if (this.total) {
                    this.total.textContent = this.formatTotal(total);
                }
            } else {
                this.renderListing(data);
            }

            if (pushUrl) {
                window.history.pushState(
                    { listingState: { ...this.state } },
                    '',
                    pushUrl
                );
            }
        } catch (error) {
            console.error(error);
            if (appendResults) {
                this.state.page = Math.max(1, this.state.page - 1);
            }
        } finally {
            if (showLoader) {
                this.root.classList.remove('is-loading');
                this.loader?.hide();
            }

            this.setLoadMoreLoading(false);

            if (!appendResults && this.canUseElastic()) {
                this.filterCountsLoaded = false;
                void this.loadFilterTermCounts({ force: true });
            }
        }
    }
}

Object.assign(
    InspirationsPage.prototype,
    listingFilterAccordionMethods,
    inspirationElasticMethods,
    listingUrlStateMethods,
    inspirationUrlStateMethods,
    listingRendererMethods,
    listingFiltersMethods,
    inspirationEventsMethods
);
